import * as THREE from 'three';
import { settings } from '@/config';
import { Lights } from '@/lights/Lights';
import { Model } from './Model';
import { Placeholder } from './Placeholder';
import { Sky } from './Sky';
import { CloudSea } from './CloudSea';
import { CameraFog } from './CameraFog';
import { LodSystem } from './LodSystem';
import { VisibilityCuller } from './VisibilityCuller';
import { SceneDepth } from './SceneDepth';
import { HeightFog } from './HeightFog';
import { GodRays } from './GodRays';
import { WaterFX } from './Water';
import { Grass } from './Grass';
import { StoryGrade } from '@/materials/storyGrade';
import { Capabilities } from '@/core/Capabilities';
import { OrbMaterial } from '@/materials/orb/OrbMaterial';
import { CustomScene1 } from '@/materials/CustomScene1';
import { createPortalHaloMaterial } from '@/materials/PortalLight';
import { BLOOM_LAYER } from '@/postprocessing/SelectiveBloom';
import {
  registerOrvaeMaterials,
  isCustomGrassName,
  isPortalLightName,
  isPlaneTextureName,
  type OrvaeMaterialControls,
} from '@/materials/orvaeScene';
import { disposeObject } from '@/utils/dispose';
import { createLogger } from '@/utils/logger';
import type { SceneManager } from '@/scene/SceneManager';
import type { MaterialManager } from '@/materials/MaterialManager';
import type { Renderer } from '@/renderer/Renderer';
import type { Camera } from '@/camera/Camera';
import type { AssetManager } from '@/loader/AssetManager';
import type { InteractionManager } from '@/interaction/InteractionManager';
import type { ScrollCameraTest } from '@/animation/ScrollCameraTest';
import type { CameraController } from '@/camera/CameraController';
import type { TickState } from '@/types';

/** Scratch colour for the story fog blend in {@link World.setStoryAtmosphere}. */
const FOG_SCRATCH = new THREE.Color();

interface WorldDeps {
  interaction: InteractionManager;
  scrollCamera: ScrollCameraTest;
  cameraController: CameraController;
  camera: Camera;
}

/** Named exceptions where a connecting staircase belongs to the scene it leads INTO,
 *  not the scene its own name/lack of suffix would suggest (2026-07-05, user
 *  correction): `Stairs`/`Stairs_Pillar` (no suffix — would default to scene 1) is
 *  the scene 1 -> 2 staircase, so it belongs to Scene 2; `Stair_Connector_S_2`/
 *  `Stair_Connector_Pillar_S2` (named "2") is actually the scene 2 -> 3 connector, so
 *  it belongs to Scene 3. */
/** Model scenes 3 and 4 are authored as ONE continuous space, so revealing 3 must also reveal 4
 *  (the user's "third and fourth is combined scene"). Both visibility paths honour this. */
const COMBINED_SCENE_FROM = 3;
const COMBINED_SCENE_TO = 4;

const SCENE_OVERRIDES: Record<string, number> = {
  // Scene 1 → 2 staircase belongs to Scene 2.
  Stairs: 2,
  Stairs_Pillar: 2,
  // The stair connectors are in SCENE 2's collection. (Their `_S_2`/`_S2` names already map to 2;
  // kept explicit for clarity.)
  Stair_Connector_S_2: 2,
  Stair_Connector_Pillar_S2: 2,
  // NOTE (2026-07-18): the Cypress trees (`Cypress_Tree_*_S_3`) belong to SCENE 3 — their `_S_3`
  // name already classifies them there, so NO override. (An earlier attempt to force them to
  // Scene 1 was WRONG and the user corrected it: cypress = scene 3, `Tree_*_S_2` planes = scene 2.)
};

/** Classifies a mesh name by scene index (1-4), from the Blender naming convention:
 *  no scene suffix = scene 1 (bare-number arch stones, `Pillar`/`Inside_stones`/
 *  `Outside_Stone`/`Water_Mesh`/doors); `_S_2`/`_S_3`/`_S_4` for scenes 2-4. Naming
 *  isn't fully consistent — `Stair_Connector_Pillar_S2` skips the second underscore —
 *  so the underscore before the digit is optional. Checks `SCENE_OVERRIDES` first for
 *  the staircase exceptions above. */
function sceneIndexForName(name: string): number {
  // Match the CLEAN name too, so a Blender duplicate suffix can't defeat an override.
  const clean = name.replace(/\.\d+$/, '');
  if (name in SCENE_OVERRIDES) return SCENE_OVERRIDES[name]!;
  if (clean in SCENE_OVERRIDES) return SCENE_OVERRIDES[clean]!;
  // The digit may be followed by `_`, a Blender `.NNN` duplicate suffix, or end-of-string.
  // ⚠️ The `.` case is essential: without it `Stage_S_4.001` failed to match and silently fell
  // back to SCENE 1, so a large Scene-4 stage mesh was being shown in chapter 1 (2026-07-18).
  const m = name.match(/_S_?(\d)(?:[._]|$)/);
  return m ? Number(m[1]) : 1;
}

/**
 * Assembles the scene: gradient sky + reflection env, the cloud sea, lights, and
 * either the loaded GLB (with name-based materials, the Blender camera, and the
 * collected interactive stones) or the procedural placeholder fallback.
 */
export class World {
  readonly lights = new Lights();
  model: Model | null = null;
  placeholder: Placeholder | null = null;
  sky: Sky | null = null;
  cloudSea: CloudSea | null = null;
  /** Depth prepass feeding the clouds' soft-particle fade (`settings.cloudSea.soft`). */
  sceneDepth: SceneDepth | null = null;
  /** Camera-hugging cloud envelope for the intro emergence / outro re-envelopment — frame-driven
   *  from `Experience.onTick`. See {@link CameraFog}. */
  cameraFog: CameraFog | null = null;
  /** Auto-LOD: per-mesh triangle reduction by camera distance. See {@link LodSystem}. */
  lodSystem: LodSystem | null = null;
  /** Screen-size culling: stops drawing meshes too small on screen to read. See
   *  {@link VisibilityCuller} — this is the draw-call lever, where {@link LodSystem} is the
   *  (much smaller) triangle one. */
  visibilityCuller: VisibilityCuller | null = null;
  heightFog: HeightFog | null = null;
  /** One box volume per `settings.godRays.instances` entry — independently placed,
   *  sharing the same appearance settings. See {@link GodRays}. */
  readonly godRaysList: GodRays[] = [];
  /** One real-time reflective `WaterComplex` surface per water mesh found in the model
   *  (scene 1's `Water_Mesh`, scene 3's `Water_S_3`, scene 4's `Water_Mesh_S_4`) — all
   *  share `settings.water`, so the whole "Water" GUI folder tunes every pool at once. */
  readonly waters: WaterFX[] = [];
  /** Animated instanced grass scattered over every custom grass mesh (see {@link Grass}). */
  grass: Grass | null = null;
  /** The Perlin shadow-flow grade shared by every scene material — story mode animates its
   *  single `amount` uniform. See {@link StoryGrade}. */
  readonly storyGrade = new StoryGrade();
  /** Stones handed to the StoneInteraction system (arch voussoirs + stage ring). */
  readonly interactiveStones: THREE.Object3D[] = [];
  /** Emissive twin meshes (`N_E`) that light up while their stone `N` is displaced. */
  readonly emissiveStones: THREE.Object3D[] = [];
  /** Static structure (pillars/doors) the stones must not penetrate. */
  readonly structureColliders: THREE.Object3D[] = [];
  /** Floor (water surface) the stones can't sink below. */
  floorY = 0.8;
  /** The vegetation billboard planes (`Bush_*`/`Cypress_Tree_*`/`Plant_*`/`Tree_*`). Collected
   *  so the cloud depth prepass can SKIP them — see {@link renderSceneDepth}. */
  readonly planeMeshes: THREE.Mesh[] = [];
  /** The `Bright_Light_White_S_*` portal-light meshes + their additive halo shells' materials
   *  (see {@link setupPortalLights}). Exposed so the debug GUI can retune the glow live. */
  readonly portalLights: THREE.Mesh[] = [];
  readonly portalHaloMaterials: THREE.ShaderMaterial[] = [];
  /** Loaded cubemap environment (sunset) — exposed so the GUI can toggle the backdrop. */
  envMap: THREE.CubeTexture | null = null;
  /** Transient additive overlay meshes (e.g. the cursor-follow glow plane) that must be
   *  EXCLUDED from the cloud depth prepass: the prepass renders with an opaque override
   *  material, so a transparent overlay would stamp a SOLID rectangle into the depth texture
   *  and the clouds would fade against an invisible quad — hard corners floating in the sky
   *  (2026-07-20, user: "I can't see through the clouds… only the glow should be visible").
   *  Same bug class as the vegetation planes, same cure. */
  readonly depthPrepassExclude: THREE.Object3D[] = [];
  /** Live hue/saturation controls for the procedural stone/pillar materials (debug GUI). */
  materialControls: OrvaeMaterialControls | null = null;
  /** The ALTERNATE sunlit material set for scene 1 (see {@link CustomScene1}) — toggled from
   *  the debug GUI against the baked atlases. */
  customScene1: CustomScene1 | null = null;
  /** The `Orave_emble_S_3` sphere + its amber energy-orb material (see {@link setupEmblem}). */
  emblemMesh: THREE.Mesh | null = null;
  orbMaterial: OrbMaterial | null = null;
  private readonly _orbSphere = new THREE.Sphere();
  /** Every mesh (+ `WaterComplex` surface) bucketed by scene index (1-4) — see
   *  {@link setActiveScene}, driven by the scroll-section system (`SceneSections.ts`). */
  readonly sceneGroups = new Map<number, THREE.Object3D[]>();

  /** The live render camera, captured in {@link build} — the distance-culling systems need its
   *  position every frame, and `update` only receives a `TickState`. */
  private camera: Camera | null = null;
  /** Scratch for the per-frame drawing-buffer size query (see {@link update}). */
  private readonly _bufferSize = new THREE.Vector2();

  private readonly log = createLogger('world');

  constructor(
    private readonly sceneManager: SceneManager,
    readonly materials: MaterialManager,
    private readonly renderer: Renderer,
  ) {
    this.sceneManager.add(this.lights.group);
  }

  build(assets: AssetManager, deps: WorldDeps): void {
    // Held for the per-frame culling systems (grass tiles), which need the live camera position.
    this.camera = deps.camera;
    // Sky/env + clouds first so reflections and atmosphere are ready.
    this.sky = new Sky(this.renderer, this.sceneManager.scene);
    const cloudTexture = assets.getTexture('cloud-puff');
    if (settings.cloudSea.enabled && cloudTexture) {
      this.cloudSea = new CloudSea(deps.camera, cloudTexture);
      this.sceneManager.add(this.cloudSea.group);
      // Soft particles need the solid scene's depth each frame (see `renderSceneDepth`).
      if (settings.cloudSea.soft.enabled) {
        this.sceneDepth = new SceneDepth(this.renderer.instance);
        this.cloudSea.setDepthTexture(this.sceneDepth.texture);
      }
    } else if (settings.cloudSea.enabled) {
      this.log.warn('Cloud puff texture failed to load — cloud sea disabled.');
    }
    // The camera smoke transition (intro emergence / checkpoint travel / restart loop) — a
    // fullscreen shader quad, fully procedural (no texture). It must never stamp the cloud
    // depth prepass — it lives ON the lens.
    if (settings.cameraFog.enabled) {
      this.cameraFog = new CameraFog();
      this.sceneManager.add(this.cameraFog.group);
      this.depthPrepassExclude.push(this.cameraFog.group);
    }
    if (settings.heightFog.enabled) this.heightFog = new HeightFog();
    this.buildEnvironment();
    this.setupGodRays(); // standalone volume — independent of the GLB, so it works either way

    // Register name-based materials before the model is processed.
    this.materialControls = registerOrvaeMaterials(this.materials, assets.loadingManager);

    const gltf = assets.getGLTF('scene');
    if (gltf) {
      this.model = new Model(gltf, this.materials);
      this.sceneManager.add(this.model.root);
      this.afterModel(this.model, deps, assets);
      return;
    }

    if (settings.world.placeholder.enabled) {
      this.log.warn('No GLB at the configured path — using procedural placeholder.');
      this.placeholder = new Placeholder(this.materials);
      this.sceneManager.add(this.placeholder.group);
      deps.interaction.setTargets(this.placeholder.interactive);
    }
  }

  /** Content root used by the intro reveal (the model, or the placeholder fallback). */
  get contentRoot(): THREE.Object3D | null {
    return this.model?.root ?? this.placeholder?.group ?? null;
  }

  /** Patch the scene geometry with the height fog so it dissolves into the cloud sea.
   *  Call AFTER the intro reveal has patched materials, so the fog chains over it. */
  applyHeightFog(): void {
    if (this.heightFog && this.contentRoot) this.heightFog.apply(this.contentRoot);
  }

  private afterModel(model: Model, deps: WorldDeps, assets: AssetManager): void {
    model.root.updateMatrixWorld(true);
    deps.interaction.setTargets([model.root]);

    this.fixSwappedNames(model.root);
    this.collectStones(model.root);
    this.keepEmissiveTwinsVisible();
    this.disableBakedShadows(model.root);
    this.collectColliders(model.root);
    this.setupWater(model.root, assets);
    this.setupGrass(model.root);
    this.setupEmblem(model.root);
    this.setupPortalLights(model.root);
    this.collectPlaneMeshes(model.root);
    this.collectSceneGroups(model.root);

    // AUTO-LOD (2026-07-23) — built AFTER every collector above so the exclusion set is
    // complete: interactive/emissive stones (pose-driven), water (own shader + Reflector),
    // vegetation planes (alpha-cutout quads, trivial), portal lights, the emblem orb, and the
    // custom-grass source meshes (hidden/replaced by the Grass system). Everything else heavy
    // gets distance-swapped index buffers. Runs async only for the wasm ready handshake.
    if (settings.lod.enabled) {
      const exclude = new Set<THREE.Object3D>([
        ...this.interactiveStones,
        ...this.emissiveStones,
        ...this.planeMeshes,
        ...this.portalLights,
      ]);
      for (const water of this.waters) exclude.add(water.mesh);
      model.root.traverse((object) => {
        const raw = (object.userData.rawName as string | undefined) ?? object.name;
        if (isCustomGrassName(raw) || /emble/i.test(raw)) exclude.add(object);
      });
      this.lodSystem = new LodSystem(deps.camera);
      void this.lodSystem.build(model.root, exclude);
    }

    // SCREEN-SIZE CULLING (2026-08-17). A SMALLER exclusion set than the LOD system's above,
    // because the two systems fail differently: swapping a mesh's GEOMETRY breaks anything that
    // poses it, whereas hiding a mesh that is a thousand units away breaks nothing.
    //
    //  - The 70 ARCH STONES (+ emissive twins) ARE included. `StoneInteraction` moves them, but it
    //    only writes `visible` once during setup, and the culler recomputes each mesh's world
    //    centre every pass — so a moving mesh is culled correctly. They're all chapter-1 geometry,
    //    so this is what stops them being drawn through chapters 2-4.
    //  - The VEGETATION PLANES are excluded. They're alpha-cutout quads whose bounding sphere is
    //    large next to the visible silhouette, so the pixel test is a poor fit for them, and a
    //    tree blinking out is exactly the kind of thing that gets noticed. ~20 draw calls is not
    //    worth that risk.
    //  - Water, portal lights, the emblem orb and the grass source planes own their own
    //    visibility, same as for the LOD system.
    if (settings.visibilityCulling.enabled) {
      const exclude = new Set<THREE.Object3D>([...this.planeMeshes, ...this.portalLights]);
      for (const water of this.waters) exclude.add(water.mesh);
      model.root.traverse((object) => {
        const raw = (object.userData.rawName as string | undefined) ?? object.name;
        if (isCustomGrassName(raw) || /emble/i.test(raw)) exclude.add(object);
      });
      this.visibilityCuller = new VisibilityCuller(deps.camera);
      this.visibilityCuller.build(model.root, exclude);
    }

    // The alternate SUNLIT material set for scene 1 (2026-07-20, user's reference frames) —
    // registered now (it snapshots the baked materials for restore), applied only if the
    // checkbox is on. Both looks stay resident; the GUI swaps them live.
    // First pass now; `Experience` runs it again once the stone system has built its own
    // materials. Both land before the warm-up compile, so no recompile ever hitches a live frame.
    this.applyStoryGrade();

    this.customScene1 = new CustomScene1(this.renderer, this.sceneManager.scene);
    this.customScene1.register(model.root);
    if (settings.customScene1.enabled) this.customScene1.apply();
    // "Load once": show every scene from the first frame (2026-07-11, user request).
    // Otherwise start with only Scene 1 visible and let the scroll sections reveal the rest.
    this.setActiveScene(settings.scenes.loadAllAtOnce ? Number.POSITIVE_INFINITY : 1);

    const hasBakedAnim = model.cameras.length > 0 && model.mixer !== null && model.animations.length > 0;

    if (hasBakedAnim) {
      // STRIP the PLANT planes' animation tracks BEFORE the mixer starts (2026-07-23). The
      // GLB exports one action per OBJECT, and every mixer.setTime() (every camera scrub,
      // every frame) re-asserted those nodes' authored pose — silently stomping
      // `settings.planes.placement`. ONLY the three Plant_*_S_3 meshes are stripped: they are
      // the placement-owned ones. The CYPRESS trees keep their tracks — they carry a REAL
      // baked sway animation from Blender the user wants playing (v2 of this fix, 2026-07-23:
      // v1 stripped every vegetation plane and killed that sway — "the cypress animation
      // stopped"). Bushes/trees likewise stay mixer-owned.
      for (const clip of model.animations) {
        clip.tracks = clip.tracks.filter((track) => {
          const node = track.name.split('.')[0] ?? '';
          return !/^Plant_\d+_S_\d+$/i.test(node);
        });
      }
      // Animated baked camera: scrub by scroll progress (production pipeline).
      model.autoUpdateMixer = false;
      model.playAll();
      const duration = Math.max(...model.animations.map((clip) => clip.duration));
      const bakedCamera = model.cameras[0]!;
      // Lock in the configured lens (mm) ONCE — ScrollCameraTest otherwise reads the
      // GLB's own baked FOV every frame, silently ignoring `focalLength`.
      const focal = settings.camera.focalLength;
      if (focal && bakedCamera instanceof THREE.PerspectiveCamera) {
        // VIEWPORT aspect, not the baked camera's (2026-07-21): deriving "35mm" against the
        // Blender render aspect produced a slightly different fov than the live per-section
        // focal maths (viewport-based) — and the controller visibly eased between the two.
        // One aspect basis everywhere ⇒ 35 is 35, constant from boot through the fly-in.
        deps.camera.setFocalLength(focal, deps.camera.instance.aspect);
        deps.cameraController.targetFov = deps.camera.instance.fov;
      }
      deps.scrollCamera.useBakedCamera({
        root: model.root,
        camera: bakedCamera,
        mixer: model.mixer!,
        duration,
        lockFov: focal !== null,
      });
      deps.scrollCamera.enabled = true;
      // Start the render camera EXACTLY on the first animation frame, then SNAP onto it — so
      // when the preloader clears the viewer just sees frame 0, not a swoop in from the default
      // start position (the "weird backfall" — 2026-07-13, user).
      deps.scrollCamera.sampleAt(0);
      deps.cameraController.commit();
      this.log.info(`Baked camera scrub enabled (clip ${duration.toFixed(2)}s).`);
    } else if (settings.camera.useModelCamera && model.cameras.length) {
      this.adoptModelCamera(model.cameras[0]!, deps);
    } else if (settings.world.model.frameOnLoad) {
      this.frameCamera(deps.cameraController, model.getBounds());
    }
  }

  /** Use the static Blender camera's exact world transform + FOV. */
  private adoptModelCamera(source: THREE.Camera, deps: WorldDeps): void {
    source.updateWorldMatrix(true, false);
    const position = new THREE.Vector3();
    const quaternion = new THREE.Quaternion();
    const scale = new THREE.Vector3();
    source.matrixWorld.decompose(position, quaternion, scale);

    // Dolly straight back along the camera's view axis so it isn't right on the arch.
    if (settings.camera.dollyBack) {
      const back = new THREE.Vector3(0, 0, 1).applyQuaternion(quaternion);
      position.addScaledVector(back, settings.camera.dollyBack);
    }

    deps.cameraController.targetPosition.copy(position);
    deps.cameraController.targetQuaternion.copy(quaternion);
    if (source instanceof THREE.PerspectiveCamera) {
      // Keep the Blender camera's position/orientation; set the lens to the configured
      // focal length (e.g. 35mm), else preserve its exact authored FOV/framing.
      const focal = settings.camera.focalLength;
      if (focal) deps.camera.setFocalLength(focal, source.aspect);
      else deps.camera.useAuthoredFraming(source.fov, source.aspect);
      deps.cameraController.targetFov = deps.camera.instance.fov;
    }
    deps.cameraController.commit();
    deps.scrollCamera.enabled = false;
    this.log.info('Using the Blender camera (static).');
  }

  /** Undo reversed Blender naming: swap `N` ↔ `N_E` for the configured stones. */
  private fixSwappedNames(root: THREE.Object3D): void {
    for (const n of settings.stones.swapEmissive) {
      const base = root.getObjectByName(n);
      const twin = root.getObjectByName(`${n}_E`);
      if (base && twin) {
        base.name = `${n}_E`;
        twin.name = n;
        this.log.info(`Swapped reversed emissive naming for stone ${n}.`);
      }
    }
  }

  private collectStones(root: THREE.Object3D): void {
    // Updated GLB naming: arch voussoirs are bare numbers (`1`..`35`), each with a
    // co-located emissive twin (`1_E`..`35_E`). Only the arch inner/outer voussoirs
    // are interactive — the `Stage_Stone_NN` base ring stays fixed.
    const isArch = (name: string) => /^\d+$/.test(name);
    const isArchEmissive = (name: string) => /^\d+_E$/i.test(name);

    root.traverse((object) => {
      if (!(object as THREE.Mesh).isMesh) return;
      const name = object.name;
      if (isArchEmissive(name)) this.emissiveStones.push(object);
      else if (isArch(name)) this.interactiveStones.push(object);
    });

    // Legacy fallback: the previous GLB used the `Stone_*` collection names.
    if (!this.interactiveStones.length) {
      const names = new Set<string>([
        ...settings.stoneCollections.outside,
        ...settings.stoneCollections.inside,
        ...settings.stoneCollections.keystones,
      ]);
      root.traverse((object) => {
        if ((object as THREE.Mesh).isMesh && names.has(object.name)) {
          this.interactiveStones.push(object);
        }
      });
    }

    this.log.info(
      `Collected ${this.interactiveStones.length} interactive stones, ${this.emissiveStones.length} emissive twins.`,
    );
  }

  private collectColliders(root: THREE.Object3D): void {
    // Everything that is NOT a moving arch voussoir (or its emissive twin, or the
    // water plane) is solid: doors, pillars, base, platform, stage ring. The arch
    // stones must not sink into any of them.
    const isArch = (name: string) => /^\d+$/.test(name);
    const isArchEmissive = (name: string) => /^\d+_E$/i.test(name);
    let floor = this.floorY;
    root.traverse((object) => {
      if (!(object as THREE.Mesh).isMesh) return;
      const name = object.name;
      if (name.includes('Water')) {
        floor = new THREE.Box3().setFromObject(object).max.y;
        return;
      }
      if (name.includes('God_Rays')) return; // light-shaft volume, not a collider
      if (isArch(name) || isArchEmissive(name)) return;
      this.structureColliders.push(object);
    });
    this.floorY = floor;
    this.log.info(
      `Collected ${this.structureColliders.length} structure colliders (floor y=${this.floorY.toFixed(2)}).`,
    );
  }

  /** Swap EVERY water mesh (scene 1's `Water_Mesh`, scene 3's `Water_S_3`, scene 4's
   *  `Water_Mesh_S_4`, ...) for its own real-time reflective `WaterComplex` surface —
   *  small scrolling-normal-map ripples + a live planar reflection of the actual scene
   *  (sky/clouds/architecture), not just a flat tinted plane (2026-07-05, user asked for
   *  "all water mesh" to feel realistic, not only scene 1's). Previously only scene 1 got
   *  this treatment "until there's a reason to run 3 live Reflectors at once" — each
   *  `WaterComplex`'s reflection pass only actually runs while ITS mesh is in the
   *  camera's view (`onBeforeRender` never fires when frustum-culled), so scenes not
   *  currently on screen along the scroll path cost nothing extra. */
  private setupWater(root: THREE.Object3D, assets: AssetManager): void {
    if (!settings.water.enabled) return;
    const sources: THREE.Mesh[] = [];
    root.traverse((object) => {
      if ((object as THREE.Mesh).isMesh && object.name.includes('Water')) {
        sources.push(object as THREE.Mesh);
      }
    });
    if (!sources.length) return;

    // Apply the boot-detected tier RIGHT HERE, not only later in `applyQualityProfile`
    // (2026-09-01, low-end pass, user: "on CPU-based [devices] water is not loading"). A
    // software-WebGL / weak device is classified 'low' before the world builds; handing that
    // tier to each pool at creation means the Reflector (and its render target) is never even
    // allocated — the pool runs the cheap fallback look from its very first frame instead of
    // spending the boot seconds trying to mirror the whole scene three times per frame.
    const bootTier = Capabilities.current?.tier ?? 'high';

    for (const source of sources) {
      const water = new WaterFX({
        source,
        renderer: this.renderer.instance,
        normalMap0: assets.getTexture('water-n0'),
        normalMap1: assets.getTexture('water-n1'),
      });
      water.mesh.name = `${water.complex ? 'WaterComplex' : 'WaterSimple'}:${source.name}`;
      water.setQuality(bootTier);
      this.sceneManager.add(water.mesh);
      source.visible = false; // hide the original flat plane
      this.waters.push(water);
    }

    // Each pool must skip every OTHER pool during its own reflection pass — see the
    // long comment on `WaterComplex.onBeforeRender` for why (recursive nested renders
    // between simultaneous Reflectors was throwing a real WebGL "feedback loop" error).
    // Complex pools only: `WaterSimple` (phones / software WebGL / LOW boot tier) has no
    // reflection pass, no peers and no hooks — nothing to wire.
    const meshes = this.waters.map((w) => w.mesh);
    for (const water of this.waters) {
      if (!water.complex) continue;
      water.complex.setPeers(meshes.filter((m) => m !== water.mesh));
      // The clouds' soft-particle fade samples the MAIN camera's depth — inside a reflection
      // render that lookup is garbage, so bypass it for the nested pass (see CloudSea note).
      water.complex.onReflectionRender = (active) => {
        this.cloudSea?.setSoftBypass(active);
        // Thin the grass for the nested reflection render — see `Grass.setReflectionActive`.
        // Without this each reflecting pool re-drew the full ~1.49M-triangle field, every frame,
        // into a target small enough that a quarter of the blades is indistinguishable.
        this.grass?.setReflectionActive(active);
      };
    }

    const kind = this.waters[0]?.complex ? 'reflective WaterComplex' : 'low-end WaterSimple';
    this.log.info(`${kind} surface installed on ${this.waters.length} water mesh(es).`);
  }

  /** Replace every custom grass mesh (`Grass_S_2`, `Grass_01..09_S_3`, `Stage_Grass_S_4`)
   *  with a field of animated instanced blades scattered over its surface, then hide the
   *  flat source plane. NOT the baked `Grass_Compund_*` tufts — those are part of the
   *  Scene 3 terrain atlas and stay put. */
  private setupGrass(root: THREE.Object3D): void {
    if (!settings.grass.enabled) return;
    const sources: THREE.Mesh[] = [];
    root.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (!mesh.isMesh) return;
      const raw = (mesh.userData.rawName as string | undefined) ?? mesh.name;
      if (isCustomGrassName(raw)) sources.push(mesh);
    });
    if (!sources.length) return;

    this.grass = new Grass();
    for (const source of sources) {
      this.grass.addPatch(source);
      if (settings.grass.ground) {
        // Keep the plane VISIBLE as brown sand/dirt under the blades (stays in the scene
        // groups so `setActiveScene` shows it).
        this.grass.applyGround(source);
      } else {
        source.visible = false; // hide the flat plane the blades replace
        source.userData.permanentlyHidden = true; // else `setActiveScene` re-shows it
      }
    }
    this.sceneManager.add(this.grass.group);
    this.log.info(`Animated grass installed on ${sources.length} mesh(es) (${this.grass.bladeTotal} blades).`);
  }

  /** The `N_E` arch meshes are DISTINCT geometry — a separate body/cap of the same voussoir,
   *  NOT a co-located duplicate of stone `N` (verified from the GLB). They belong to the
   *  `Baked_1_S_1` collection and MUST stay visible wearing that baked texture like every
   *  other arch mesh (2026-07-12, user: "not excluding _E, apply Baked_1_S_1"). So keep them
   *  visible and let `StoneInteraction` ride them along with their stone through the hover
   *  wave. (Previously, with the hover glow off, this HID them — which made the bulk of each
   *  arch stone vanish and read as "textures not applied".) */
  private keepEmissiveTwinsVisible(): void {
    for (const twin of this.emissiveStones) {
      twin.visible = true;
      twin.userData.permanentlyHidden = false;
    }
  }

  /** Baked meshes wear an unlit `MeshBasicMaterial` — the lighting (and its shadows) are
   *  already IN the texture. Real-time cast shadows from them would double up on the baked
   *  ones, and basic materials can't RECEIVE shadows anyway, so switch both off to keep the
   *  baked look clean and save the shadow-map cost. */
  private disableBakedShadows(root: THREE.Object3D): void {
    root.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (mesh.isMesh && (mesh.material as THREE.Material)?.type === 'MeshBasicMaterial') {
        mesh.castShadow = false;
        mesh.receiveShadow = false;
      }
    });
  }

  /** Finds the `Orave_emble_S_3` sphere (already wearing the {@link OrbMaterial} via the
   *  resolver) and wires up the amber energy orb: seed its world-space bounding sphere (the
   *  volumetric raymarch is bounded by it) and hand it the HDR cubemap for reflection/
   *  refraction (procedural sky fallback until the cubemap loads — see {@link buildEnvironment}).
   *  `World.update` keeps the sphere + flow time current each frame. */
  private setupEmblem(root: THREE.Object3D): void {
    let emblemMesh: THREE.Mesh | null = null;
    root.traverse((object) => {
      if (emblemMesh || !(object as THREE.Mesh).isMesh) return;
      if (object.name.includes('Orave_emble')) emblemMesh = object as THREE.Mesh;
    });
    if (!emblemMesh) return;
    const mesh: THREE.Mesh = emblemMesh;

    const orb = mesh.material instanceof OrbMaterial ? mesh.material : null;
    if (!orb) {
      this.log.warn('Emblem mesh is not wearing the OrbMaterial — skipping orb setup.');
      return;
    }
    this.emblemMesh = mesh;
    this.orbMaterial = orb;

    // Shadows are meaningless for the orb (it's emissive glass); keep it out of the maps.
    mesh.castShadow = false;
    mesh.receiveShadow = false;

    this.syncOrbSphere();
    orb.setEnvMap(this.envMap); // may be null now; buildEnvironment re-sets it on load
    this.log.info('Amber energy orb installed on the emblem sphere.');
  }

  /** Refresh the orb's world-space bounding sphere from the (possibly animated) emblem mesh. */
  private syncOrbSphere(): void {
    if (!this.emblemMesh || !this.orbMaterial) return;
    const geo = this.emblemMesh.geometry;
    if (!geo.boundingSphere) geo.computeBoundingSphere();
    this._orbSphere.copy(geo.boundingSphere!);
    this.emblemMesh.updateWorldMatrix(true, false);
    this._orbSphere.applyMatrix4(this.emblemMesh.matrixWorld);
    this.orbMaterial.setSphere(this._orbSphere.center, this._orbSphere.radius);
  }

  /** Bucket every mesh (+ each `WaterComplex` reflective surface) by scene index, for
   *  the scroll-section visibility system (see {@link setActiveScene}). The original
   *  flat `Water_Mesh`-style GLB planes are skipped — they're already permanently
   *  hidden in favour of the `WaterComplex` replacement (`setupWater`), which lives
   *  OUTSIDE `model.root` (added straight to the scene) so this traversal can't see it
   *  and each instance is bucketed separately by its own name below. */
  private collectSceneGroups(root: THREE.Object3D): void {
    root.traverse((object) => {
      if (!(object as THREE.Mesh).isMesh || object.name.includes('Water')) return;
      // Skip meshes replaced/retired by a custom system (grass source planes, the hidden
      // `_E` emissive twins) — bucketing them would let `setActiveScene` re-show them.
      if (object.userData.permanentlyHidden) return;
      // Classify by the ORIGINAL Blender name, not three.js's sanitized `.name`: GLTFLoader
      // strips the dot from a duplicate suffix (`Stage_S_4.001` → `Stage_S_4001`), which
      // breaks the `_S_4` scene-suffix match and would mis-bucket that mesh into Scene 1
      // (harmless while `loadAllAtOnce` shows everything, but it would hide it otherwise).
      const raw = (object.userData.rawName as string | undefined) ?? object.name;
      this.pushToSceneGroup(sceneIndexForName(raw), object);
    });
    for (const water of this.waters) {
      this.pushToSceneGroup(sceneIndexForName(water.mesh.name), water.mesh);
    }
    for (const [scene, objects] of this.sceneGroups) {
      this.log.info(`Scene ${scene}: ${objects.length} mesh(es).`);
    }
  }

  private pushToSceneGroup(scene: number, object: THREE.Object3D): void {
    let list = this.sceneGroups.get(scene);
    if (!list) {
      list = [];
      this.sceneGroups.set(scene, list);
    }
    list.push(object);
  }

  /** Show the given scene AND every scene before it (cumulative — reaching Scene 3
   *  still shows Scenes 1 and 2 behind/below you along the path), hiding only scenes
   *  further ahead that haven't been reached yet — driven by the scroll-section
   *  system (2026-07-05, user correction: NOT exclusive single-scene visibility).
   *  Hiding a parent (e.g. the emblem mesh) also hides its children (the glow shell)
   *  for free: three.js skips rendering an invisible object's descendants entirely,
   *  no separate bucketing needed for those. */
  setActiveScene(index: number): void {
    // Scenes 3 and 4 are ONE combined space (2026-07-19, user: "when you load scene three at the
    // same time load scene 4 also"). `showSceneExclusive` already paired them; the cumulative path
    // did not, so arriving in Chapter 3 left scene 4's half of the same environment missing.
    const effective = index >= COMBINED_SCENE_FROM ? Math.max(index, COMBINED_SCENE_TO) : index;
    for (const [scene, objects] of this.sceneGroups) {
      const visible = scene <= effective;
      for (const object of objects) {
        // Record the gate as well as applying it: `VisibilityCuller` reads `sceneVisible` so the
        // two systems compose (`visible = gate && !culled`) instead of overwriting each other.
        object.userData.sceneVisible = visible;
        object.visible = visible;
      }
    }
    // Grass lives outside `sceneGroups` (its sources are `permanentlyHidden` and the instanced
    // meshes are added straight to the scene), so it has to be gated explicitly — otherwise
    // future scenes' grass stays on the horizon while their architecture is correctly hidden.
    this.grass?.setActiveScene(effective);
  }

  /** EXCLUSIVE scene visibility for the cinematic flow (2026-07-13): show ONLY the given
   *  scene's meshes and hide the rest — so each scroll section reveals one fresh scene
   *  behind the white-smoke transition, not a cumulative pile. Exception: Scenes 3 and 4 are
   *  shown TOGETHER (the user's "third and fourth is combined scene"). Meshes retired by a
   *  custom system are already excluded from `sceneGroups`, so they stay hidden. */
  showSceneExclusive(index: number): void {
    const visible =
      index >= COMBINED_SCENE_FROM ? new Set([COMBINED_SCENE_FROM, COMBINED_SCENE_TO]) : new Set([index]);
    for (const [scene, objects] of this.sceneGroups) {
      const on = visible.has(scene);
      for (const object of objects) {
        object.userData.sceneVisible = on; // see the note in `setActiveScene`
        object.visible = on;
      }
    }
    this.grass?.showSceneExclusive(index); // see the note in `setActiveScene`
  }

  /** Give every `Bright_Light_White_S_*` mesh its glow (2026-07-17). The mesh itself already
   *  wears the shared {@link PortalLightMaterial} (from the name resolver); this adds the
   *  ADDITIVE HALO SHELL as a child — a slightly enlarged copy of the same geometry that
   *  fades out from the silhouette, which is what makes the light visibly spill past the
   *  mesh's edge (there's no bloom pass to do it for us).
   *
   *  GOTCHA: this GLB bakes each mesh's position INTO ITS VERTICES, so a node's origin is
   *  nowhere near its geometry. Scaling the shell about the origin would fling it across the
   *  scene. Scaling about the geometry's bbox centre `c` instead = scale `s` + a compensating
   *  offset of `c * (1 - s)`. */
  private setupPortalLights(root: THREE.Object3D): void {
    const cfg = settings.portalLight;
    if (!cfg.enabled) return;

    const sources: THREE.Mesh[] = [];
    root.traverse((object) => {
      const mesh = object as THREE.Mesh;
      const raw = (mesh.userData.rawName as string | undefined) ?? mesh.name;
      if (mesh.isMesh && isPortalLightName(raw)) sources.push(mesh);
    });
    if (!sources.length) return;

    for (const source of sources) {
      this.portalLights.push(source);
      // Keep the lights OUT of the scene-group visibility system: `collectSceneGroups` skips
      // `permanentlyHidden` meshes, so `setActiveScene` can never re-show one behind the
      // GUI toggle's back. Their visibility is owned solely by `settings.portalLight.visible`.
      source.userData.permanentlyHidden = true;
      source.visible = cfg.visible;
      if (!cfg.halo.enabled) continue;

      const haloMat = createPortalHaloMaterial();
      this.portalHaloMaterials.push(haloMat);
      const shell = new THREE.Mesh(source.geometry, haloMat);
      shell.name = `${source.name}:halo`;
      // Inherit the source's Blender name so `collectSceneGroups` buckets the shell into the
      // SAME scene as its parent (its own `:halo` name defeats the `_S_n` suffix regex).
      shell.userData.rawName = source.userData.rawName ?? source.name;
      source.geometry.computeBoundingBox();
      const c = source.geometry.boundingBox!.getCenter(new THREE.Vector3());
      const s = cfg.halo.scale;
      shell.scale.setScalar(s);
      shell.position.copy(c).multiplyScalar(1 - s); // scale about `c`, not the baked origin
      shell.renderOrder = 2; // draw after opaque so the additive glow lays on top
      shell.castShadow = false;
      shell.receiveShadow = false;
      source.add(shell); // child ⇒ inherits the source's transform + visibility for free
    }

    this.log.info(`Portal light glow installed on ${sources.length} mesh(es).`);
  }

  /** Opt the EMISSIVE objects into the bloom layer — these are the ONLY things that glow: the
   *  portal light (+ its additive halo) and the emblem orb. The stones' hover-glow overlays are
   *  marked in `StoneInteraction.setEmission`, where they're created. Nothing else is drawn into
   *  the bloom source, so the sky and every texture are untouchable. */
  markBloomObjects(): void {
    this.setGlowEnabled(settings.selectiveBloom.enabled);
  }

  /**
   * Turn the emissive glow on/off LIVE (the "postprocessing" GUI toggle).
   *
   * Two things have to move together, or the toggle only half-works:
   *  1. the BLOOM_LAYER opt-in — what the bloom pass is allowed to draw at all;
   *  2. the additive HALO shells — these glow on their own, with no bloom pass involved, so
   *     leaving them visible would still show a glow after the user turned it off.
   *
   * The portal light's own core mesh is deliberately NOT touched here: its visibility is a
   * separate user-facing setting ({@link setPortalLightsVisible}, "show light mesh").
   */
  setGlowEnabled(on: boolean): void {
    if (this.emblemMesh) {
      if (on) this.emblemMesh.layers.enable(BLOOM_LAYER);
      else this.emblemMesh.layers.disable(BLOOM_LAYER);
    }
    for (const light of this.portalLights) {
      light.traverse((child) => {
        if (on) child.layers.enable(BLOOM_LAYER);
        else child.layers.disable(BLOOM_LAYER);
        // Halo shells glow WITHOUT the bloom pass — they must be hidden outright when off.
        if (child.name.endsWith(':halo')) child.visible = on && settings.portalLight.halo.enabled;
      });
    }
  }

  /** The heaviest subtrees to hide during the bloom pass — none are on the bloom layer, so this is
   *  purely to shorten that pass's scene walk (the cloud sea alone is hundreds of sprites). */
  bloomTraversalSkip(): THREE.Object3D[] {
    const out: THREE.Object3D[] = [];
    if (this.cloudSea) out.push(this.cloudSea.group);
    if (this.grass) out.push(this.grass.group);
    return out;
  }

  /** Find the vegetation billboard planes so the cloud depth prepass can skip them (see
   *  {@link renderSceneDepth} for why that matters). */
  private collectPlaneMeshes(root: THREE.Object3D): void {
    root.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (!mesh.isMesh) return;
      const raw = (mesh.userData.rawName as string | undefined) ?? mesh.name;
      if (!isPlaneTextureName(raw)) return;
      // HOME transform = the authored Blender pose. `settings.planes.placement` stores DELTAS
      // from this, so re-applying is idempotent and all-zeros always means "as authored".
      mesh.userData.placementKey = raw;
      mesh.userData.homePos = mesh.position.clone();
      mesh.userData.homeRot = mesh.rotation.clone();
      this.planeMeshes.push(mesh);
    });
    this.applyPlanePlacement();
  }

  /** Push `settings.planes.placement` (per-mesh position delta + per-axis rotation deltas, in
   *  the mesh's parent space) onto the vegetation planes. Pass a raw mesh name to update just
   *  that plane (the GUI does), or nothing to apply all. */
  applyPlanePlacement(name?: string): void {
    for (const mesh of this.planeMeshes) {
      const key = mesh.userData.placementKey as string;
      if (name && key !== name) continue;
      const cfg = settings.planes.placement[key];
      const home = mesh.userData.homePos as THREE.Vector3;
      const homeRot = mesh.userData.homeRot as THREE.Euler;
      mesh.position.set(
        home.x + (cfg?.x ?? 0),
        home.y + (cfg?.y ?? 0),
        home.z + (cfg?.z ?? 0),
      );
      mesh.rotation.set(
        homeRot.x + THREE.MathUtils.degToRad(cfg?.rotXDeg ?? 0),
        homeRot.y + THREE.MathUtils.degToRad(cfg?.rotYDeg ?? 0),
        homeRot.z + THREE.MathUtils.degToRad(cfg?.rotZDeg ?? 0),
        homeRot.order,
      );
    }
  }

  /**
   * Capture the solid scene's depth for the clouds' soft-particle fade. Call ONCE per frame,
   * from the render step, BEFORE the main render.
   *
   * The exclusion list is doing real work, not micro-optimising:
   *  - WATER is mandatory. `WaterComplex.onBeforeRender` drives a `Reflector` that re-renders
   *    the whole scene into its own target; leaving the pools visible here would fire a full
   *    nested reflection render inside the depth pass, every frame, once per pool.
   *  - The CLOUDS themselves must not occlude each other in the depth buffer, or every puff
   *    would fade against the one behind it and the sea would dissolve.
   *  - GRASS is ~141k instanced blades sitting on top of the platform, nowhere near the cloud
   *    band — pure cost with no visual effect on the fade.
   *  - The SKY dome is a backdrop at radius 900; it would just fill the buffer with far depth.
   *  - The VEGETATION PLANES are mandatory, and were a real bug (2026-07-17, user: "from those
   *    plane mesh … i can't see the clouds background"). They're ALPHA-CUTOUT quads, but the
   *    prepass swaps in a plain `overrideMaterial` that has no map and no alphaTest — so each
   *    plane wrote its FULL RECTANGLE into the depth texture. The clouds then read "solid
   *    geometry here" across the whole quad and faded themselves to nothing behind it, punching
   *    a cloud-free rectangle around every plant and tree. Any future alpha-cutout mesh must be
   *    excluded here too, for the same reason.
   */
  renderSceneDepth(camera: THREE.Camera): void {
    // Also gated on the LIVE setting so the low quality tier can switch this whole extra scene
    // render off at runtime (the cloud shader bypasses its fade via `uSoftAmount` to match).
    if (!this.sceneDepth || !this.cloudSea || !settings.cloudSea.soft.enabled) return;
    const exclude: THREE.Object3D[] = [this.cloudSea.group, ...this.planeMeshes, ...this.depthPrepassExclude];
    if (this.sky) exclude.push(this.sky.mesh);
    if (this.grass) exclude.push(this.grass.group);
    for (const water of this.waters) exclude.push(water.mesh);
    this.sceneDepth.render(this.sceneManager.scene, camera, exclude);
  }

  /** Size the depth prepass to the renderer's DRAWING BUFFER (canvas px × pixel ratio) — the
   *  cloud shader derives its lookup from `gl_FragCoord`, which is in those same units. */
  resizeSceneDepth(): void {
    if (!this.sceneDepth || !this.cloudSea) return;
    const size = this.renderer.instance.getDrawingBufferSize(new THREE.Vector2());
    this.sceneDepth.setSize(size.x, size.y, settings.cloudSea.soft.resolutionScale);
    this.cloudSea.setResolution(size.x, size.y);
  }

  /** Show/hide the portal light meshes (+ their halo children, which follow the parent's
   *  visibility for free). Driven by the "Portal Light → show light mesh" GUI toggle. */
  setPortalLightsVisible(visible: boolean): void {
    for (const mesh of this.portalLights) mesh.visible = visible;
  }

  /** Re-apply `settings.portalLight.halo.scale` to every halo shell (debug GUI). Repeats the
   *  scale-about-the-bbox-centre maths from {@link setupPortalLights} — see the gotcha there. */
  refreshPortalHaloScale(): void {
    const s = settings.portalLight.halo.scale;
    for (const source of this.portalLights) {
      source.geometry.computeBoundingBox();
      const c = source.geometry.boundingBox!.getCenter(new THREE.Vector3());
      for (const child of source.children) {
        if (!child.name.endsWith(':halo')) continue;
        child.scale.setScalar(s);
        child.position.copy(c).multiplyScalar(1 - s);
      }
    }
  }

  /** Standalone volumetric yellow-gold light shafts — one box per
   *  `settings.godRays.instances` entry, not sourced from the GLB. */
  private setupGodRays(): void {
    if (!settings.godRays.enabled) return;
    for (const transform of settings.godRays.instances) {
      const god = new GodRays(transform);
      this.godRaysList.push(god);
      this.sceneManager.add(god.mesh);
    }
    this.log.info(`God rays: ${this.godRaysList.length} volume(s) installed (see settings.godRays).`);
  }

  /** Optional cubemap environment map (sunset) for PBR reflections + optional backdrop. */
  private buildEnvironment(): void {
    const cfg = settings.water.envMap;
    if (!cfg.enabled) return;
    const loader = new THREE.CubeTextureLoader().setPath(cfg.path);
    loader.load(
      ['right.png', 'left.png', 'top.png', 'bot.png', 'front.png', 'back.png'],
      (cube) => {
        cube.colorSpace = THREE.SRGBColorSpace;
        this.envMap = cube;
        this.sceneManager.scene.environment = cube;
        if (cfg.asBackground) this.sceneManager.scene.background = cube;
        this.orbMaterial?.setEnvMap(cube); // give the orb real HDR reflections/refraction
        this.log.info('Cubemap environment map (sunset) applied.');
      },
      undefined,
      () => this.log.warn(`Env map not found at ${cfg.path} — keeping the Sky environment.`),
    );
  }

  private frameCamera(controller: CameraController, box: THREE.Box3): void {
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z) || 1;
    const distance = maxDim * 1.8;
    const position = new THREE.Vector3(
      center.x + distance,
      center.y + distance * 0.6,
      center.z + distance,
    );
    controller.lookFrom(position, center);
    controller.commit();
  }

  /**
   * Patch every scene material with the Perlin shadow-flow grade (2026-08-18).
   *
   * Called during build and AGAIN after the stone-interaction system exists — hence the
   * idempotence. `StoryGrade.attach` guards with a WeakSet, so re-running only ever picks up
   * materials created since the last call.
   *
   * ⚠️ TRAVERSAL, NOT A CURATED LIST. This used to walk `materialControls.baked/planes/fallback`
   * plus the grass, and it missed 70 materials: every `N_E` emissive arch twin builds its OWN
   * material inside `StoneInteraction`, which runs after the world is built. The result was an
   * architecture that darkened while parts of the arch stayed lit. Walking the scene graph
   * cannot fall behind a system that creates materials later.
   *
   * Two things are deliberately skipped:
   *  - `ShaderMaterial` (sky, clouds, orb, fog): no `<map_fragment>` chunk to hook. The sky and
   *    clouds are graded by their own uniforms instead — see {@link setStoryAtmosphere}.
   *  - Anything not using NORMAL blending. Additive overlays (the stones' hover glow, the portal
   *    halo) are LIGHT rather than surface; darkening a glow is backwards, and they must keep
   *    reading as emission inside the dark.
   */
  applyStoryGrade(): void {
    if (!settings.storyMode.enabled) return;
    let n = 0;
    const attach = (material: THREE.Material | null | undefined): void => {
      if (!material) return;
      if ((material as THREE.ShaderMaterial).isShaderMaterial) return;
      if (material.blending !== THREE.NormalBlending) return;
      this.storyGrade.attach(material);
      n += 1;
    };
    const visit = (object: THREE.Object3D): void => {
      const mesh = object as THREE.Mesh;
      if (!mesh.isMesh && !(mesh as unknown as THREE.InstancedMesh).isInstancedMesh) return;
      const material = mesh.material;
      if (Array.isArray(material)) for (const m of material) attach(m);
      else attach(material);
    };
    this.model?.root.traverse(visit);
    this.grass?.group.traverse(visit);
    if (this.grass) for (const m of this.grass.gradeMaterials) attach(m);
    this.log.info(`story grade covers ${n} material slot(s).`);
  }

  /**
   * Blend the ATMOSPHERE toward the story's night palette — the user's "sky changes to dark".
   * `k` 0 = authored, 1 = full story. Kept separate from the material grade because the sky dome
   * and cloud sea are custom shaders the Perlin dissolve cannot hook.
   */
  setStoryAtmosphere(k: number): void {
    this.sky?.setStoryDark(k);
    this.cloudSea?.setStoryDark(k);
    // SCENE FOG (2026-09-02 v3): THREE's distance fog mixes in AFTER the material grade, so
    // without this every far surface was washed back toward the daytime cream haze — pale
    // slabs floating in the black. The fog colour lerps to the story's near-black instead,
    // so distance sinks geometry INTO the dark, exactly like the reference. Re-derived from
    // settings each call (same rationale as the sky blend above).
    const fog = this.sceneManager.scene.fog as THREE.Fog | null;
    if (fog) {
      fog.color
        .set(settings.fog.color)
        .lerp(FOG_SCRATCH.set(settings.storyMode.grade.fogColor), k);
    }
  }

  /** VOICE PULSE (2026-09-02): 0..1, eased by Experience — the ember energy on the meshes and
   *  the sky's flowing lines swell while the narrator speaks and sink in her silences. */
  setStoryPulse(v: number): void {
    this.storyGrade.pulse.value = v;
    this.sky?.setStoryPulse(v);
  }

  update(tick: TickState): void {
    this.model?.update(tick);
    this.sky?.update(tick.delta); // story-mode fluid sky drift (no-op outside the grade)
    this.cloudSea?.update(tick);
    this.grass?.update(tick, this.camera?.instance);
    this.lodSystem?.update(tick.delta);
    // Viewport HEIGHT in device pixels — the culler's pixel threshold is measured in the same
    // units the frame is actually rasterised at, so a lower render scale culls slightly harder,
    // which is exactly right.
    this.visibilityCuller?.update(
      tick.delta,
      this.renderer.instance.getDrawingBufferSize(this._bufferSize).y,
    );
    for (const god of this.godRaysList) god.update(tick);
    for (const water of this.waters) water.update(tick);

    // Portal lights: advance the shared pulse on the core + every halo shell together.
    if (this.materialControls && this.portalLights.length) {
      this.materialControls.portalLight.update(tick.delta);
      for (const halo of this.portalHaloMaterials) {
        halo.uniforms.uTime!.value += tick.delta;
      }
    }
    
    if (this.orbMaterial) {
      this.orbMaterial.update(tick.delta); // advance the flowing energy
      this.syncOrbSphere(); // the emblem is animated — keep the raymarch bounds locked to it
    }
  }

  dispose(): void {
    this.lights.dispose();
    this.sky?.dispose();
    this.cloudSea?.dispose();
    this.sceneDepth?.dispose();
    this.heightFog?.dispose();
    this.grass?.dispose();
    this.cameraFog?.dispose();
    this.lodSystem?.dispose();
    this.visibilityCuller?.dispose();
    for (const god of this.godRaysList) god.dispose();
    for (const water of this.waters) water.dispose();
    if (this.model) disposeObject(this.model.root);
    this.placeholder?.dispose();
  }
}
